import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api";
import { Lock, Mail, Globe, Eye, EyeOff } from "lucide-react";

const INDIGO = "#667eea";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState<"fr" | "en">("fr");

  const t = lang === "fr"
    ? {
        title: "Connexion",
        subtitle: "Identifiez-vous pour accéder au dashboard",
        email: "Adresse e-mail",
        password: "Mot de passe",
        button: "Se connecter",
        loading: "Connexion…",
        footer: "Plateforme paiement multi-tenant",
      }
    : {
        title: "Sign in",
        subtitle: "Log in to access your dashboard",
        email: "Email address",
        password: "Password",
        button: "Sign in",
        loading: "Signing in…",
        footer: "Multi-tenant payment platform",
      };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const me = await auth.login(email, password);
      if (me.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      const isMerchantUser = !!me.merchant_id;
      const home = isMerchantUser
        ? "/merchant/overview"
        : me.role === "admin"
        ? "/admin/merchants"
        : me.role === "finance"
        ? "/wallets"
        : me.role === "it"
        ? "/it/switch-logs"
        : "/payments";
      navigate(home, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur réseau. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  if (auth.status === "authenticated") {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="relative min-h-screen min-h-[100svh] bg-white overflow-hidden" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* Top bar (lang selector only — le branding est dans le card central) */}
      <div className="absolute top-4 right-4 lg:top-8 lg:right-10 z-20 flex items-center justify-end gap-3">
        {/* Language selector */}
        <div className="relative inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1 py-1 shadow-sm shrink-0">
          <Globe size={13} className="text-slate-400 ml-1.5 mr-0.5 hidden sm:inline" />
          <button
            onClick={() => setLang("fr")}
            className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              lang === "fr" ? "text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            style={lang === "fr" ? { background: INDIGO } : {}}
          >
            FR
          </button>
          <button
            onClick={() => setLang("en")}
            className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              lang === "en" ? "text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            style={lang === "en" ? { background: INDIGO } : {}}
          >
            EN
          </button>
        </div>
      </div>

      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
           style={{ background: "radial-gradient(circle, #667eea 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
           style={{ background: "radial-gradient(circle, #764ba2 0%, transparent 70%)" }} />

      {/* Center form */}
      <div className="relative z-10 min-h-screen min-h-[100svh] flex items-center justify-center px-3 sm:px-4 py-24 sm:py-12">
        <div className="w-full max-w-md">
          <div
            className="bg-white rounded-2xl p-6 sm:p-10"
            style={{ boxShadow: "0 20px 60px rgba(102, 126, 234, 0.15), 0 8px 24px rgba(0, 0, 0, 0.05)" }}
          >
            {/* Dual logos Liquid × Moko centrés au dessus de Connexion */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 mb-5 sm:mb-6">
              <img src="/liquid-logo.png" alt="Liquid" className="h-9 sm:h-10 w-auto object-contain" />
              <span className="text-[#e4e6ef] font-light text-xl sm:text-2xl select-none" aria-hidden="true">×</span>
              <img src="/moko-logo.png" alt="Moko Afrika" className="h-9 sm:h-10 w-auto object-contain rounded-lg" />
            </div>

            <div className="mb-6 sm:mb-7 text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] mb-2">{t.title}</h1>
              <div className="text-sm text-[#8c8ca0]">{t.subtitle}</div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">{t.email}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@gofreshpay.com"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#f5f6fa] border border-[#f5f6fa] rounded-xl text-[#1a1a2e] placeholder-[#a1a5b7] focus:bg-white focus:border-[#667eea] focus:outline-none transition-colors text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">{t.password}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full pl-11 pr-11 py-3.5 bg-[#f5f6fa] border border-[#f5f6fa] rounded-xl text-[#1a1a2e] placeholder-[#a1a5b7] focus:bg-white focus:border-[#667eea] focus:outline-none transition-colors text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? (lang === "fr" ? "Masquer le mot de passe" : "Hide password") : (lang === "fr" ? "Afficher le mot de passe" : "Show password")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#a1a5b7] hover:text-[#4a4a6a] transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 border border-red-100">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3.5 rounded-xl text-white font-semibold text-base overflow-hidden group transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
              >
                <span className="relative z-10">{loading ? t.loading : t.button}</span>
                {/* Shine effect */}
                <span
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                  }}
                />
              </button>
            </form>
          </div>

          <div className="text-center mt-6 text-xs text-[#a1a5b7]">{t.footer}</div>
        </div>
      </div>
    </div>
  );
}

